import { useMemo } from 'react';

/**
 * Adds a totals row to the beginning of the rows array if totalsRow is provided
 * 
 * @param {Array} rows - Raw data rows
 * @param {Object} totalsRow - Optional mapping of column keys to totals values
 * @returns {Array} Rows with totals row prepended if totalsRow is provided
 */
function addTotalsRow(rows, totalsRow) {
  if (!totalsRow) {
    return rows;
  }
  const totalsRowObject = {
    id: 'DataGridTable-totals-row',
    isTotalsRow: true
  };
  return [totalsRowObject, ...rows];
}

/**
 * Hook to process rows by adding totals row, applying filters, sorting, and row expansion
 * 
 * @param {Array} rows - Raw data rows
 * @param {Object} totalsRow - Optional mapping of column keys to totals values
 * @param {Function} applyFilters - Function to apply filters to rows
 * @param {Function} applySorting - Function to apply sorting to rows
 * @param {Function} insertChildRows - Function to insert child rows for expanded rows
 * @returns {Array} Processed rows ready for display
 */
export function useProcessedRows(rows, totalsRow, applyFilters, applySorting, insertChildRows) {
  const processedRows = useMemo(() => {
    let result = addTotalsRow(rows, totalsRow);
    result = applyFilters(result);
    result = applySorting(result);

    // Insert child rows for expanded rows - also sorts and filters child rows
    result = insertChildRows(result);
    return result;
  }, [rows, totalsRow, applyFilters, applySorting, insertChildRows]);
  return processedRows;
}